import escapeStringRegexp from 'escape-string-regexp';
import { NumberRoundingMethod, UserRole } from 'src/app/services/api.services';
import { isNullOrUndefined, TimeZone } from 'src/app/shared/utils';
import { DataType } from '../../dataModel/DataType';
import { createDateTimeValue, createDateValue, createTimeValue, DateTimeValue, roundBigNumberValue, roundNumericValue } from '../expr';
import { VM } from './VM';

export type IdentifierHandler = (vm: VM) => any;

export class IdentifierSymbol {
  public readonly handler: IdentifierHandler;

  constructor(
    public readonly name: string,
    handlerOrValue?: IdentifierHandler | any) {
    this.handler =
      isNullOrUndefined(handlerOrValue)
        ? (vm => this)
        : ((typeof handlerOrValue === 'function')
            ? handlerOrValue
            : (vm => handlerOrValue));
  }
}

export type FunctionHandler = (vm: VM) => any;

export class FunctionSymbol {
  constructor(
    public readonly name: string,
    public readonly handler: FunctionHandler) { }
}


export class Symbols {
  private readonly identifiers = new Map<string, IdentifierSymbol>();
  private readonly functions = new Map<string, FunctionSymbol>();

  /**
   * Adds an identifier with the given name.
   * If @handlerOrValue is not provided, the identifier will return itself.
   * If @handlerOrValue is not a function, the identifier will return the value.
   * If @handlerOrValue is a function, the identifier will invoke the function. */
  private addIdentifier(name: string);
  private addIdentifier(name: string, handler: IdentifierHandler);
  private addIdentifier(name: string, value: Exclude<any, IdentifierHandler>);
  private addIdentifier(name: string, handlerOrValue?: any) {
    let symbol = new IdentifierSymbol(name, handlerOrValue);
    this.identifiers.set(name.toUpperCase(), symbol);
    return symbol;
  }

  private addFunction(name: string, handler: FunctionHandler) {
    let symbol = new FunctionSymbol(name, handler);
    this.functions.set(name.toUpperCase(), symbol);
    return symbol;
  }

  getIdentifier(name: string): IdentifierSymbol {
    return this.identifiers.get(name.toUpperCase());
  }

  getFunction(name: string): FunctionSymbol {
    return this.identifiers.get(name.toUpperCase());
  }

  
  readonly VALUE = this.addIdentifier('VALUE', vm => {
    return vm.evaluator.context.value;
  });

  readonly BLANK = this.addIdentifier('BLANK', '\n');
  readonly TRUE = this.addIdentifier('TRUE', true);
  readonly FALSE = this.addIdentifier('FALSE', false);

  readonly DISTINCT = this.addIdentifier('DISTINCT');
  readonly TREAT_NULL_AS_BLANK = this.addIdentifier('TREAT_NULL_AS_BLANK');
  
  readonly ASC = this.addIdentifier('ASC');
  readonly DESC = this.addIdentifier('DESC');

  readonly BOOLEAN = this.addIdentifier('BOOLEAN', DataType.Boolean);
  readonly INTEGER = this.addIdentifier('INTEGER', DataType.Integer);
  readonly NUMBER = this.addIdentifier('NUMBER', DataType.Number);
  readonly DECIMAL = this.addIdentifier('DECIMAL', DataType.Decimal);
  readonly DATEONLY = this.addIdentifier('DATEONLY', DataType.DateOnly);
  readonly TIMEONLY = this.addIdentifier('TIMEONLY', DataType.TimeOnly);
  readonly DATETIME = this.addIdentifier('DATETIME', DataType.DateTime);
  readonly TEXT = this.addIdentifier('TEXT', DataType.Text);


  readonly ANY = this.addFunction('ANY', vm => {
    return vm.args().some(a => vm.getBool(a, true));
  });

  readonly ALL = this.addFunction('ALL', vm => {
    return !vm.args().some(a => vm.getBool(a, true));
  });

  readonly IN = this.addFunction('IN', vm => {
    let [v, l] = vm.args(2, [, vm.getList]);
    return l.some(x => vm.eq(x, v));
  });


  readonly IF = this.addFunction('IF', vm => {
    let [c, t, f] = vm.args(3, [vm.convertToBool, ,]);
    return c ? t : f;
  });

  readonly CHOOSE = this.addFunction('CHOOSE', vm => {
    let [i, l] = vm.args(2, [vm.convertToNumber, vm.getList]);
    return vm.indexList(l, i);
  });


  readonly IS_NULL = this.addFunction('IS_NULL', vm => {
    return vm.isNull(vm.args(1)[0]);
  });

  readonly IF_NULL = this.addFunction('IF_NULL', vm => {
    let [v, a] = vm.args(2);
    return vm.isNull(v) ? a : v;
  });


  readonly IS_BLANK = this.addFunction('IS_BLANK', vm => {
    return vm.isBlank(vm.args(1)[0]);
  });

  readonly IF_BLANK = this.addFunction('IF_BLANK', vm => {
    let [v, a] = vm.args(2);
    return vm.isBlank(v) ? a : v;
  });


  readonly IS_NULL_OR_BLANK = this.addFunction('IS_NULL_OR_BLANK', vm => {
    return vm.isNullOrBlank(vm.args(1)[0]);
  });

  readonly IF_NULL_OR_BLANK = this.addFunction('IF_NULL_OR_BLANK', vm => {
    let [v, a] = vm.args(2);
    return vm.isNullOrBlank(v) ? a : v;
  });


  readonly IS_NUMBER = this.addFunction('IS_NUMBER', vm => {
    let v = vm.args(1)[0];
    return vm.getDataType(v).isNumeric && !vm.isNaN(v);
  });


  readonly MIN = this.addFunction('MIN', vm => {
    let list = vm.selectNumbers(vm.args({ min: 2 }));
    return vm.switchNumeric(list[0], {
      number: () => Math.min(...<any>list),
      bigNumber: () => BigNumber.min(...<any>list),
    });
  });

  readonly MAX = this.addFunction('MAX', vm => {
    let list = vm.selectNumbers(vm.args({ min: 2 }));
    return vm.switchNumeric(list[0], {
      number: () => Math.max(...<any>list),
      bigNumber: () => BigNumber.max(...<any>list),
    });
  });


  readonly RANDOM = this.addFunction('RANDOM', vm => {
    return vm.args([
      {
        args: 0, then: () => {
          return Math.random();
        }
      },
      {
        args: 2, then: (vm, [min, max]) => {
          // min + (Math.random() * (max - min))
          return vm.add(min, vm.mul(Math.random(), vm.sub(max, min)));
        }
      }
    ]);
  });

  readonly RANDOM_INT = this.addFunction('RANDOM_INT', vm => {
    return vm.toInt(this.RANDOM.handler(vm));
  });

  
  readonly TO_BOOL = this.addFunction('TO_INT', vm => {
    return vm.convertToBool(vm.args(1)[0]);
  });

  readonly TO_INT = this.addFunction('TO_INT', vm => {
    return vm.convertToBigNumber(vm.args(1)[0], true);
  });

  readonly TO_NUMBER = this.addFunction('TO_NUMBER', vm => {
    return vm.convertToBigNumber(vm.args(1)[0], false);
  });

  readonly TO_DECIMAL = this.addFunction('TO_DECIMAL', vm => {
    return vm.convertToBigNumber(vm.args(1)[0], false);
  });

  readonly TO_DATE = this.addFunction('TO_DATE', vm => {
    return vm.convertToDateOnly(vm.args(1)[0]);
  });

  readonly TO_TIME = this.addFunction('TO_TIME', vm => {
    return vm.convertToTimeOnly(vm.args(1)[0]);
  });

  readonly TO_DATETIME = this.addFunction('TO_DATETIME', vm => {
    return vm.convertToDateTime(vm.args(1)[0], vm.ident(vm.symbols.TIMEZONE));
  });

  readonly TO_TEXT = this.addFunction('TO_TEXT', vm => {
    return vm.getText(vm.args(1)[0]);
  });


  readonly CONVERT = this.addFunction('CONVERT', vm => {
    let [v, t] = vm.args(2, [, vm.getDataTypeFromSymbol]);
    return vm.convert(v, t);
  });

  readonly PARSE = this.addFunction('PARSE', vm => {
    return this.CONVERT.handler(vm);
  });


  readonly ORGANISATION = this.addIdentifier('ORGANISATION', vm => {
    return vm.evaluator.context.organisation;
  });

  readonly CURRENCY_NAME = this.addIdentifier('CURRENCY_NAME', vm => {
    return vm.evaluator.context.currency?.displayName
      ?? vm.evaluator.context.currency?.currencyName
      ?? vm.evaluator.context.currency?.currencyCode;
  });

  readonly CURRENCY_CODE = this.addIdentifier('CURRENCY_CODE', vm => {
    return vm.evaluator.context.currency?.currencyCode;
  });

  readonly CURRENCY_SYMBOL = this.addIdentifier('CURRENCY_SYMBOL', vm => {
    return vm.evaluator.context.currency?.symbol;
  });


  readonly BUSINESS_USER_ROLE = this.addIdentifier('BUSINESS_USER_ROLE', 'Business User');
  readonly ADMIN_ROLE         = this.addIdentifier('ADMIN_ROLE', 'Admin');
  readonly SUPPORT_ADMIN_ROLE = this.addIdentifier('SUPPORT_ADMIN_ROLE', 'Support Admin');
  readonly SUPER_ADMIN_ROLE   = this.addIdentifier('SUPER_ADMIN_ROLE', 'Super Admin');
  readonly DEVELOPER_ROLE     = this.addIdentifier('DEVELOPER_ROLE', 'Developer');

  readonly USER = this.addIdentifier('USER', vm => {
    return vm.evaluator.context.user?.name;
  });

  readonly USER_ROLE = this.addIdentifier('USER_ROLE', vm => {
    let role = vm.evaluator.context.user?.role;
    if (isNullOrUndefined(role))
      return null;

    switch (role) {
      case UserRole.ClientUser:
        return vm.ident(vm.symbols.BUSINESS_USER_ROLE);

      case UserRole.InsightsUser:
        return vm.ident(vm.symbols.ADMIN_ROLE);

      case UserRole.ClientAdmin:
        return vm.ident(vm.symbols.SUPPORT_ADMIN_ROLE);

      case UserRole.InsightsAdmin:
        return vm.ident(vm.symbols.SUPER_ADMIN_ROLE);

      case UserRole.Developer:
        return vm.ident(vm.symbols.DEVELOPER_ROLE);
    }
  });

  readonly IS_IMPERSONATING = this.addIdentifier('IS_IMPERSONATING', vm => {
    return vm.evaluator.context.user?.isImpersonating;
  });

  readonly USER_NOW = this.addIdentifier('USER_NOW', vm => {
    return vm.evaluator.context.user?.timestamp;
  });

  readonly USER_NOW_UTC = this.addIdentifier('USER_NOW_UTC', vm => {
    return vm.evaluator.context.user?.timestampUtc;
  });

  readonly USER_TIMEZONE = this.addIdentifier('USER_TIMEZONE', vm => {
    return vm.evaluator.context.user?.tz;
  });

  readonly VISUAL_NAME = this.addIdentifier('VISUAL_NAME', vm => {
    return vm.evaluator.context.visualData?.name;
  });
  

  readonly HAS_PARAM = this.addFunction('HAS_PARAM', vm => {
    if (!vm.evaluator.context.parameterResolver)
      return null;

    let [name] = vm.args(1, [vm.getText]);
    name = name.toUpperCase();
    return vm.evaluator.context.parameterResolver(name);
  });

  readonly PARAM = this.addFunction('PARAM', vm => {
    if (!vm.evaluator.context.parameterResolver)
      return null;

    let [name, defaultValue] = vm.args({ min: 1, max: 2 }, [vm.getText]);
    name = name.toUpperCase();
    let param = vm.evaluator.context.parameterResolver(name);
    if (param === undefined)
      throw new Error(`Could not find parameter '${name}'`);

    return param;
  });


  readonly E = this.addIdentifier('E', Math.E);
  readonly PI = this.addIdentifier('PI', Math.PI);

  readonly AWAY_FROM_ZERO = this.addIdentifier('AWAY_FROM_ZERO');
  readonly NEAREST_INTEGER = this.addIdentifier('NEAREST_INTEGER');


  readonly IS_ZERO = this.addFunction('IS_ZERO', vm => {
    return vm.args([
      {
        args: 1, then: (vm, [n]) => vm.neq(vm.abs(n), 0)
      },
      {
        args: 2, then: (vm, [n, t]) => vm.lte(vm.abs(vm.sub(n, 0)), t)
      }
    ]);
  });
  readonly IS_APPROX = this.addFunction('IS_APPROX', vm => {
    return vm.args([
      {
        args: 2, then: (vm, [n, x]) => vm.lte(vm.abs(vm.sub(n, 0)), 0.0001)
      },
      {
        args: 3, then: (vm, [n, x, t]) => vm.lte(vm.abs(vm.sub(n, 0)), t)
      }
    ]);
  });

  readonly TO_DEGREES = this.addFunction('TO_DEGREES', vm => {
    return vm.mul(vm.args(1)[0], 57.2957795131);
  });

  readonly TO_RADIANS = this.addFunction('TO_RADIANS', vm => {
    return vm.div(vm.args(1)[0], 57.2957795131);
  });

  readonly ACOS = this.addFunction('ACOS', vm => {
    return Math.acos(vm.convertToNumber(vm.args(1)[0]));
  });
  readonly ASIN = this.addFunction('ASIN', vm => {
    return Math.asin(vm.convertToNumber(vm.args(1)[0]));
  });
  readonly ATAN = this.addFunction('ATAN', vm => {
    return Math.atan(vm.convertToNumber(vm.args(1)[0]));
  });
  readonly ATAN2 = this.addFunction('ATAN2', vm => {
    let [y, x] = vm.args(2, [vm.convertToNumber]);
    return Math.atan2(y, x);
  });
  readonly COS = this.addFunction('COS', vm => {
    return Math.cos(vm.convertToNumber(vm.args(1)[0]));
  });
  readonly COT = this.addFunction('COT', vm => {
    return 1.0 / Math.tan(vm.convertToNumber(vm.args(1)[0]));
  });
  readonly SIN = this.addFunction('SIN', vm => {
    return Math.sin(vm.convertToNumber(vm.args(1)[0]));
  });
  readonly TAN = this.addFunction('TAN', vm => {
    return Math.tan(vm.convertToNumber(vm.args(1)[0]));
  });


  readonly ABS = this.addFunction('ABS', vm => {
    return vm.abs(vm.args(1)[0]);
  });

  readonly SIGN = this.addFunction('SIGN', vm => {
    let n = vm.args(1)[0];
    return vm.conditional(vm.lt(n, 0), -1, vm.conditional(vm.gt(n, 0), +1, 0));
  });


  readonly LOG = this.addFunction('LOG', vm => {
    return Math.log(vm.convertToNumber(vm.args(1)[0]));
  });

  readonly LOG10 = this.addFunction('LOG10', vm => {
    return Math.log10(vm.convertToNumber(vm.args(1)[0]));
  });


  readonly POW = this.addFunction('POW', vm => {
    let [n, e] = vm.args(2);
    return vm.pow(n, e);
  });

  readonly SQRT = this.addFunction('SQRT', vm => {
    let n = vm.args(1)[0];
    return vm.switchNumeric(n, {
      number: (vm, n) => Math.sqrt(n),
      bigNumber: (vm, n) => n.sqrt()
    });
  });

  readonly SQUARE = this.addFunction('SQUARE', vm => {
    let n = vm.args(1)[0];
    return vm.mul(n, n);
  });


  readonly CLAMP = this.addFunction('CLAMP', vm => {
    let [n, min, max] = vm.args(3);
    return vm.conditional(vm.lt(n, min), min, vm.conditional(vm.gt(n, max), max, n));
  });

  readonly CLAMP01 = this.addFunction('CLAMP01', vm => {
    let n = vm.args(1)[0];
    return vm.conditional(vm.lt(n, 0), 0, vm.conditional(vm.gt(n, 1), 1, n));
  });


  readonly LERP = this.addFunction('LERP', vm => {
    let [a, b, t] = vm.args(3);
    let r = vm.sub(b, a);
    return vm.add(a, vm.mul(t, r));
  });

  readonly REMAP = this.addFunction('REMAP', vm => {
    let [n, ax, ay, bx, by] = vm.args(5);
    let ar = vm.sub(ay, ax);
    let br = vm.sub(by, bx);
    let t  = vm.sub(vm.div(n, ar), ax);
    return vm.add(bx, vm.mul(t, br));
  });

  readonly PERCENT_OF = this.addFunction('PERCENT_OF', vm => {
    let [v, t] = vm.args(2);
    return vm.mul(vm.div(v, t), 100);
  });


  readonly CEIL = this.addFunction('CEIL', vm => {
    let n = vm.args(1)[0];
    return vm.switchNumeric(n, {
      number: (vm, n) => Math.ceil(n),
      bigNumber: (vm, n) => n.integerValue(BigNumber.ROUND_CEIL)
    });
  });

  readonly FLOOR = this.addFunction('FLOOR', vm => {
    let n = vm.args(1)[0];
    return vm.switchNumeric(n, {
      number: (vm, n) => Math.ceil(n),
      bigNumber: (vm, n) => n.integerValue(BigNumber.ROUND_FLOOR)
    });
  });

  readonly TRUNCATE = this.addFunction('TRUNCATE', vm => {
    let [n, p] = vm.args({ min: 1, max: 2 });

    if (p)
      p = vm.getNumber(p);

    return vm.switchNumeric(n, {
      number: (vm, n) => roundNumericValue(n, {
        method: NumberRoundingMethod.Truncate,
        precision: p
      }),
      bigNumber: (vm, n) => (vm, n) => roundBigNumberValue(n, {
        method: NumberRoundingMethod.Truncate,
        precision: p
      }),
    });
  });

  readonly ROUND = this.addFunction('ROUND', vm => {
    let [n, p, m] = vm.args({ min: 1, max: 3 }, [,, vm.getIdent]);

    if (p)
      p = vm.getNumber(p);

    let method =
      m === vm.symbols.AWAY_FROM_ZERO  ? NumberRoundingMethod.AwayFromZero :
      m === vm.symbols.NEAREST_INTEGER ? NumberRoundingMethod.Nearest      : NumberRoundingMethod.Truncate;

    return vm.switchNumeric(n, {
      number: (vm, n) => roundNumericValue(n, {
        method,
        precision: p
      }),
      bigNumber: (vm, n) => (vm, n) => roundBigNumberValue(n, {
        method,
        precision: p
      }),
    });
  });


  readonly NEW_LINE = this.addIdentifier('NEW_LINE', '\n');

  readonly STR_LOWER = this.addIdentifier('STR_LOWER', vm => {
    let x = vm.args(1, [vm.getText])[0] as string;
    return x?.toLowerCase();
  });

  readonly STR_UPPER = this.addIdentifier('STR_UPPER', vm => {
    let x = vm.args(1, [vm.getText])[0] as string;
    return x?.toUpperCase();
  });

  readonly STR_LEN = this.addIdentifier('STR_LEN', vm => {
    let x = vm.args(1, [vm.getText])[0] as string;
    return x?.length;
  });

  readonly STR_FIND = this.addIdentifier('STR_FIND', vm => {
    let [v, t] = vm.args(2, [vm.getText]) as string[];
    if (isNullOrUndefined(v) || isNullOrUndefined(t))
      return null;

    return v.toLowerCase().indexOf(t.toLowerCase()) + 1;
  });

  readonly STR_ISLIKE = this.addIdentifier('STR_ISLIKE', vm => {
    let [v, p] = vm.args(2, [vm.getText]) as string[];
    if (isNullOrUndefined(v) || isNullOrUndefined(p))
      return null;

    let regexValue = escapeStringRegexp(p)
      .replace(/%/g, '.*')
      .replace(/_/g, '.');

    return (new RegExp(`^${regexValue}$`, 'gi').test(v));
  });

  readonly STR_REPLACE = this.addIdentifier('STR_REPLACE', vm => {
    let [v, t, r] = vm.args(3, [vm.getText]) as string[];
    if (isNullOrUndefined(v) || isNullOrUndefined(t))
      return null;

    return v.replace(new RegExp(escapeStringRegexp(r), 'gi'), r ?? '');
  });

  readonly STR_REPLACE_FIRST = this.addIdentifier('STR_REPLACE_FIRST', vm => {
    let [v, t, r] = vm.args(3, [vm.getText]) as string[];
    if (isNullOrUndefined(v) || isNullOrUndefined(t))
      return null;

    return v.replace(new RegExp(escapeStringRegexp(r), 'i'), r ?? '');
  });

  readonly TRIM = this.addIdentifier('TRIM', vm => {
    let [v] = vm.args(1, [vm.getText]) as string[];
    if (isNullOrUndefined(v))
      return null;

    return v.trim();
  });

  readonly TRIM_START = this.addIdentifier('TRIM_START', vm => {
    let [v] = vm.args(1, [vm.getText]) as string[];
    if (isNullOrUndefined(v))
      return null;

    return v.trimStart();
  });

  readonly TRIM_END = this.addIdentifier('TRIM_END', vm => {
    let [v] = vm.args(1, [vm.getText]) as string[];
    if (isNullOrUndefined(v))
      return null;

    return v.trimEnd();
  });

  readonly CONCAT = this.addIdentifier('CONCAT', vm => {
    return vm.cat('', ...vm.args());
  });

  readonly STR_EXTRACT = this.addIdentifier('STR_EXTRACT', vm => {
    let [v, s, l] = vm.args(3, [vm.getText, vm.getIntNumber]) as [string, number, number];
    if (isNullOrUndefined(v) || isNullOrUndefined(s) || isNullOrUndefined(l))
      return null;

    return v.substring(s - 1, s - 1 + l);
  });

  readonly STR_INSERT = this.addIdentifier('STR_INSERT', vm => {
    let [v, s, r] = vm.args(3, [vm.getText, vm.getIntNumber, vm.getText]) as [string, number, string];
    if (isNullOrUndefined(v) || isNullOrUndefined(s) || isNullOrUndefined(r))
      return null;

    return v.substring(0, s) + (r ?? '') + v.substring(s + 1);
  });

  readonly STR_JOIN = this.addIdentifier('STR_JOIN', vm => {
    let separator = vm.getText(vm.args({ min: 2 })[0]);
    return vm.cat(separator, ...vm.args().slice(1));
  });

  readonly PAD_LEFT = this.addIdentifier('PAD_LEFT', vm => {
    let [v, t, c] = vm.args(3, [vm.getText, vm.getText, vm.getIntNumber]) as [string, string, number];
    if (isNullOrUndefined(v) || isNullOrUndefined(c))
      return null;

    return v.padStart(c, t ?? ' ');
  });

  readonly PAD_RIGHT = this.addIdentifier('PAD_RIGHT', vm => {
    let [v, t, c] = vm.args(3, [vm.getText, vm.getText, vm.getIntNumber]) as [string, string, number];
    if (isNullOrUndefined(v) || isNullOrUndefined(c))
      return null;

    return v.padEnd(c, t ?? ' ');
  });

  readonly STR_LEFT = this.addIdentifier('STR_LEFT', vm => {
    let [v, c] = vm.args(2, [vm.getText, vm.getText, vm.getIntNumber]) as [string, number];
    if (isNullOrUndefined(v) || isNullOrUndefined(c))
      return null;

    return v.substring(0, c - 1);
  });

  readonly STR_RIGHT = this.addIdentifier('STR_RIGHT', vm => {
    let [v, c] = vm.args(2, [vm.getText, vm.getText, vm.getIntNumber]) as [string, number];
    if (isNullOrUndefined(v) || isNullOrUndefined(c))
      return null;

    return v.substring(v.length - c, c);
  });


  readonly MONDAY = this.addIdentifier('MONDAY', 1);
  readonly TUESDAY = this.addIdentifier('TUESDAY', 2);
  readonly WEDNESDAY = this.addIdentifier('WEDNESDAY', 3);
  readonly THURSDAY = this.addIdentifier('THURSDAY', 4);
  readonly FRIDAY = this.addIdentifier('FRIDAY', 5);
  readonly SATURDAY = this.addIdentifier('SATURDAY', 6);
  readonly SUNDAY = this.addIdentifier('SUNDAY', 7);

  readonly JANUARY = this.addIdentifier('JANUARY', 1);
  readonly FEBRUARY = this.addIdentifier('FEBRUARY', 2);
  readonly MARCH = this.addIdentifier('MARCH', 3);
  readonly APRIL = this.addIdentifier('APRIL', 4);
  readonly MAY = this.addIdentifier('MAY', 5);
  readonly JUNE = this.addIdentifier('JUNE', 6);
  readonly JULY = this.addIdentifier('JULY', 7);
  readonly AUGUST = this.addIdentifier('AUGUST', 8);
  readonly SEPTEMBER = this.addIdentifier('SEPTEMBER', 9);
  readonly OCTOBER = this.addIdentifier('OCTOBER', 10);
  readonly NOVEMBER = this.addIdentifier('NOVEMBER', 11);
  readonly DECEMBER = this.addIdentifier('DECEMBER', 12);

  readonly TIME_FRACTION = this.addIdentifier('TIME_FRACTION', 'MILLISECOND');
  readonly SECOND = this.addIdentifier('SECOND', 'SECOND');
  readonly MINUTE = this.addIdentifier('MINUTE', 'MINUTE');
  readonly HOUR = this.addIdentifier('HOUR', 'HOUR');
  readonly DAY = this.addIdentifier('DAY', 'DATE');
  readonly WEEKDAY = this.addIdentifier('WEEKDAY', 'DAY');
  readonly WEEK = this.addIdentifier('WEEK', 'WEEK');
  readonly MONTH = this.addIdentifier('MONTH', 'MONTH');
  readonly QUARTER = this.addIdentifier('QUARTER', 'QUARTER');
  readonly YEAR = this.addIdentifier('YEAR', 'YEAR');
  readonly TIMEZONE_OFFSET = this.addIdentifier('TIMEZONE_OFFSET', 'TZ');

  readonly NOW = this.addIdentifier('NOW', vm => vm.evaluator.context.timestamp);
  readonly NOW_UTC = this.addIdentifier('NOW_UTC', vm => vm.evaluator.context.timestampUtc);
  readonly TIMEZONE = this.addIdentifier('TIMEZONE', vm => vm.evaluator.context.tz);

  readonly MAKE_DATE = this.addIdentifier('MAKE_DATE', vm => {
    let [y, m, d] = vm.args(3, [vm.getIntNumber]);
    return createDateValue(y, m, d);
  });

  readonly MAKE_TIME = this.addIdentifier('MAKE_TIME', vm => {
    let [h, m, s, ms] = vm.args({ min: 3, max: 4 }, [vm.getIntNumber]);
    return createTimeValue(h, m, s, ms ?? 0);
  });

  readonly MAKE_DATETIME = this.addIdentifier('MAKE_DATETIME', vm => {
    return vm.args([
      {
        args: 2,
        then: (vm, d, t) => {
          d = vm.convertToDateOnly(d);
          t = vm.convertToTimeOnly(t);

          return createDateTimeValue(d, t, vm.ident(vm.symbols.TIMEZONE));
        }
      },
      {
        args: { min: 6, max: 7 },
        then: (vm, y, m, d, hr, min, sec, ms) => {
          [y, m, d, hr, min, sec, ms] = [
            vm.getIntNumber(y),
            vm.getIntNumber(m),
            vm.getIntNumber(d),
            vm.getIntNumber(hr),
            vm.getIntNumber(min),
            vm.getIntNumber(sec),
            vm.getIntNumber(ms) ?? 0
          ];

          return createDateTimeValue(
            createDateValue(y, m, d),
            createTimeValue(hr, min, sec, ms),
            vm.ident(vm.symbols.TIMEZONE));
        }
      }
    ]);
  });

  readonly TIMESTAMP_ADD = this.addIdentifier('TIMESTAMP_ADD', vm => {
    throw new Error(`Not implemented`, { cause: this.TIMESTAMP_ADD });
  });

  readonly TIMESTAMP_DIFF = this.addIdentifier('TIMESTAMP_DIFF', vm => {
    throw new Error(`Not implemented`, { cause: this.TIMESTAMP_DIFF });
  });

  readonly GET_TIME_FRACTION = this.addIdentifier('GET_TIME_FRACTION', vm => {
    return vm.getTimeOnly(vm.args(1)[0], true)?.fraction.value;
  });

  readonly GET_SECOND = this.addIdentifier('GET_SECOND', vm => {
    return vm.getTimeOnly(vm.args(1)[0], true)?.second.value;
  });

  readonly GET_MINUTE = this.addIdentifier('GET_MINUTE', vm => {
    return vm.getTimeOnly(vm.args(1)[0], true)?.minute.value;
  });

  readonly GET_HOUR = this.addIdentifier('GET_HOUR', vm => {
    return vm.getTimeOnly(vm.args(1)[0], true)?.hour.value;
  });

  readonly GET_DAY = this.addIdentifier('GET_DAY', vm => {
    return vm.getDateOnly(vm.args(1)[0], true)?.day.value;
  });

  readonly GET_WEEKDAY = this.addIdentifier('GET_WEEKDAY', vm => {
    return vm.getDateOnly(vm.args(1)[0], true)?.weekday.value;
  });

  readonly GET_WEEKDAY_NAME = this.addIdentifier('GET_WEEKDAY_NAME', vm => {
    return vm.getDateOnly(vm.args(1)[0], true)?.weekday.name;
  });

  readonly GET_MONTH = this.addIdentifier('GET_MONTH', vm => {
    return vm.getDateOnly(vm.args(1)[0], true)?.month.value;
  });

  readonly GET_MONTH_NAME = this.addIdentifier('GET_MONTH_NAME', vm => {
    return vm.getDateOnly(vm.args(1)[0], true)?.month.name;
  });

  readonly GET_QUARTER = this.addIdentifier('GET_QUARTER', vm => {
    return vm.getDateOnly(vm.args(1)[0], true)?.quarter.value;
  });

  readonly GET_QUARTER_NAME = this.addIdentifier('GET_QUARTER_NAME', vm => {
    return vm.getDateOnly(vm.args(1)[0], true)?.quarter.name;
  });

  readonly GET_YEAR = this.addIdentifier('GET_YEAR', vm => {
    return vm.getDateOnly(vm.args(1)[0], true)?.year.value;
  });

  readonly GET_DATE = this.addIdentifier('GET_DATE', vm => {
    return vm.getDateOnly(vm.args(1)[0], true);
  });

  readonly GET_TIME = this.addIdentifier('GET_TIME', vm => {
    return vm.getTimeOnly(vm.args(1)[0], true);
  });


  readonly GET_YEAR_AND_MONTH = this.addIdentifier('GET_YEAR_AND_MONTH', vm => {
    let d = vm.getDateOnly(vm.args(1)[0], true);
    return `${d.year.value}-${('' + d.month.value).padStart(2, '0')}`;
  });

  readonly GET_YEAR_AND_QUARTER = this.addIdentifier('GET_YEAR_AND_QUARTER', vm => {
    let d = vm.getDateOnly(vm.args(1)[0], true);
    return `${d.year.value}-${d.quarter.name}`;
  });


  readonly CONVERT_TIMEZONE = this.addIdentifier('CONVERT_TIMEZONE', vm => {
    let [v, tz] =
      vm.args(2, [vm.convertToDateTime, vm.getText, vm.getText]) as [DateTimeValue, string];

    let tz1 = TimeZone.from(v.timezone);
    let tz2 = TimeZone.from(tz);

    return createDateTimeValue(tz2.fromUtc(tz1.toUtc(v.toDate())), tz2.id);
  });
}

export const SYMBOLS = new Symbols();
